import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { QR } from "../lib/qr";
import { CopyButton } from "../lib/CopyButton";
import { useDialog } from "../lib/useDialog";
import { useSlugCheck } from "../lib/slugCheck";
import { refreshSignedIn } from "../lib/session";
import EmbedTab from "./EmbedTab";
import ExportBar from "./ExportBar";
import { count, money, timeAgo, whenLabel } from "../lib/format";
import { Pager } from "../lib/Pager";
import { DEFAULT_PAGE_SIZE, pageCount, type StatusFilter } from "../../shared/requestList";
import { bankEmailNote } from "../../shared/bankEmailStatus";
import {
  CheckIcon,
  ClockIcon,
  CloseIcon,
  DashIcon,
  ExternalIcon,
  Logo,
  StatusChip,
  StatusIcon,
} from "../lib/ui";
import type { DashboardStats, PaymentRequestRow, SafeOrg } from "../lib/types";

interface DashboardData {
  org: SafeOrg;
  stats: DashboardStats;
  /** The newest bank email and what became of it; null before the first. */
  lastEmail: { at: string; outcome: string | null } | null;
}
interface CreatedRequest {
  id: string;
  reference: string;
  amount: number | null;
  description: string | null;
  payUrl: string;
  payload: string;
}

type Tab = "requests" | "embed" | "settings";

/** Remembers that the "put a button on your site" prompt has done its job (dismissed, or a first
 *  code copied). Per browser; losing it just shows the prompt once more. */
const EMBED_SEEN_KEY = "cp.embedPromptSeen";
function readEmbedSeen(): boolean {
  try {
    return localStorage.getItem(EMBED_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}
type Confirming = { kind: "delete" | "paid"; id: string } | null;
interface Notice {
  ok: boolean;
  text: string;
}

const FILTERS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "pending", label: "Waiting" },
  { id: "confirmed", label: "Paid" },
  { id: "expired", label: "Expired" },
];

export default function Dashboard() {
  const nav = useNavigate();
  const origin = window.location.origin;

  const [data, setData] = useState<DashboardData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState<Tab>("requests");

  // The list: one page at a time from the server, so the numbers and the
  // pager are right however many requests there are.
  const [list, setList] = useState<{ rows: PaymentRequestRow[]; total: number } | null>(null);
  const [listBusy, setListBusy] = useState(false);
  const [listError, setListError] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [query, setQuery] = useState(""); // what is in the search box
  const [search, setSearch] = useState(""); // what has been sent to the server
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [refreshKey, setRefreshKey] = useState(0);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [embedSeen, setEmbedSeen] = useState(readEmbedSeen);

  // New request form
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [payerName, setPayerName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [created, setCreated] = useState<CreatedRequest | null>(null);

  // Row actions — one inline question open at a time.
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailError, setEmailError] = useState("");
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Rail
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateMsg, setRotateMsg] = useState<Notice | null>(null);

  const amountInputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const ledgerRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const createCloseRef = useRef<HTMLButtonElement>(null);

  // The form stays inline; only a successful creation's result (QR, reference, copy-link) pops
  // up in a dialog. Focus lands in Amount on close, not on "Create request", which is disabled the
  // instant the form resets and so can't take focus; it also lets the next request be typed at once.
  const createModalRef = useDialog(created !== null, () => setCreated(null), createCloseRef, amountInputRef);

  const load = useCallback(async () => {
    const res = await api.get<DashboardData>("/api/dashboard");
    if (!res.ok) {
      setLoadError(true);
      return;
    }
    setLoadError(false);
    setData(res.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** After anything changes a request: the numbers up top and the page below. */
  const refresh = useCallback(() => {
    load();
    setRefreshKey((k) => k + 1);
  }, [load]);

  // The search box waits a beat after typing, then starts again from page 1.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [query]);

  const signedIn = data !== null;
  useEffect(() => {
    if (!signedIn) return;
    let stale = false;
    setListBusy(true);
    const qs = new URLSearchParams({ status: filter, page: String(page), pageSize: String(pageSize) });
    if (search) qs.set("q", search);
    api.get<{ requests: PaymentRequestRow[]; total: number }>(`/api/requests?${qs}`).then((res) => {
      if (stale) return;
      setListBusy(false);
      if (!res.ok) {
        setListError(res.error);
        return;
      }
      setListError("");
      // Rows were deleted and this page no longer exists: go to the last one.
      if (res.data.requests.length === 0 && res.data.total > 0 && page > 1) {
        setPage(pageCount(res.data.total, pageSize));
        return;
      }
      setList({ rows: res.data.requests, total: res.data.total });
    });
    return () => {
      stale = true;
    };
  }, [signedIn, filter, search, page, pageSize, refreshKey]);

  function changeFilter(next: StatusFilter) {
    setFilter(next);
    setPage(1);
  }

  function changePageSize(size: number) {
    setPageSize(size);
    setPage(1);
  }

  /** Pager clicks land you at the top of the list, not wherever the buttons were. */
  function goToPage(next: number) {
    setPage(next);
    window.requestAnimationFrame(() => ledgerRef.current?.scrollIntoView({ block: "start" }));
  }

  async function logout() {
    await api.post("/api/auth/logout", {});
    refreshSignedIn();
    nav("/");
  }

  async function createRequest(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError("");
    setCreated(null);
    const res = await api.post<CreatedRequest>("/api/requests", {
      amount: amount === "" ? null : amount,
      description,
      payerName,
    });
    setCreating(false);
    if (!res.ok) {
      setCreateError(res.error);
      return;
    }
    setCreated(res.data);
    refresh();
    setAmount("");
    setDescription("");
    setPayerName("");
  }

  function openQuestion(kind: "delete" | "paid", id: string) {
    setEditingEmailId(null);
    setConfirmingBulk(false);
    setNotice(null);
    setConfirming({ kind, id });
  }

  function cancelQuestion() {
    if (!confirming) return;
    const { kind, id } = confirming;
    setConfirming(null);
    // Back to the button that opened the question, not <body>.
    window.setTimeout(() => document.getElementById(`act-${kind}-${id}`)?.focus(), 0);
  }

  async function deleteRequest(id: string) {
    const req = list?.rows.find((r) => r.id === id);
    setBusyId(id);
    const res = await api.delete(`/api/requests/${id}`);
    setBusyId(null);
    setConfirming(null);
    if (res.ok) {
      setNotice({ ok: true, text: `Deleted ${req?.reference ?? "the request"}.` });
      searchRef.current?.focus(); // the row (and its button) is gone
    } else {
      setNotice({ ok: false, text: res.error });
    }
    refresh(); // either way what we were showing may be out of date (e.g. it just got paid)
  }

  async function markConfirmed(id: string) {
    const req = list?.rows.find((r) => r.id === id);
    setBusyId(id);
    // `manual` is what says a person asked for this; a bodyless POST isn't enough.
    const res = await api.post(`/api/requests/${id}/confirm`, { manual: true });
    setBusyId(null);
    setConfirming(null);
    if (res.ok) {
      setNotice({ ok: true, text: `Marked ${req?.reference ?? "the request"} as paid.` });
      searchRef.current?.focus();
      refresh();
    } else {
      setNotice({ ok: false, text: res.error });
    }
  }

  async function deleteExpired() {
    setBulkBusy(true);
    const res = await api.delete<{ deleted: number }>("/api/requests?status=expired");
    setBulkBusy(false);
    setConfirmingBulk(false);
    if (res.ok) {
      const n = res.data.deleted;
      setNotice({ ok: true, text: `Deleted ${n} expired request${n === 1 ? "" : "s"}.` });
      refresh();
      searchRef.current?.focus();
    } else {
      setNotice({ ok: false, text: res.error });
    }
  }

  function cancelBulk() {
    setConfirmingBulk(false);
    window.setTimeout(() => document.getElementById("delete-expired")?.focus(), 0);
  }

  function startEditEmail(id: string) {
    const req = list?.rows.find((r) => r.id === id);
    setEmailDraft(req?.payer_email ?? "");
    setEmailError("");
    setConfirming(null);
    setConfirmingBulk(false);
    setNotice(null);
    setEditingEmailId(id);
  }

  function cancelEditEmail() {
    const id = editingEmailId;
    setEditingEmailId(null);
    window.setTimeout(() => document.getElementById(`act-email-${id}`)?.focus(), 0);
  }

  async function saveEmail(id: string) {
    const trimmed = emailDraft.trim();
    if (!trimmed) {
      setEmailError("Enter an email address.");
      return;
    }
    setBusyId(id);
    setEmailError("");
    const res = await api.patch<{ payerEmail: string; receiptSent: boolean }>(
      `/api/requests/${id}/email`,
      { email: trimmed },
    );
    setBusyId(null);
    if (res.ok) {
      setEditingEmailId(null);
      refresh();
      setNotice({
        ok: true,
        text: res.data.receiptSent ? "Saved. The receipt has been sent." : "Saved the payer’s email.",
      });
      searchRef.current?.focus();
    } else {
      setEmailError(res.error);
    }
  }

  /**
   * Switching tab doesn't reset the scroll, so go to the top. When the switch didn't come from a tab
   * button (a rail link, the prompt banner) the clicked thing has disappeared, so focus the new
   * page's heading rather than nothing.
   */
  function showTab(next: Tab, viaTabButton = false) {
    setTab(next);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    if (!viaTabButton) window.requestAnimationFrame(() => headingRef.current?.focus({ preventScroll: true }));
  }

  function markEmbedSeen() {
    setEmbedSeen(true);
    try {
      localStorage.setItem(EMBED_SEEN_KEY, "1");
    } catch {
      /* private mode: the prompt just comes back next visit */
    }
  }

  async function toggleLink(next: boolean) {
    if (!data) return;
    const orgId = data.org.id;
    setLinkBusy(true);
    setLinkError("");
    setData((d) => d && { ...d, org: { ...d.org, public_link_enabled: next } });
    const res = await api.patch(`/api/orgs/${orgId}`, { publicLinkEnabled: next });
    setLinkBusy(false);
    if (!res.ok) {
      setData((d) => d && { ...d, org: { ...d.org, public_link_enabled: !next } });
      setLinkError(res.error);
    }
  }

  async function rotateAddress() {
    if (!data) return;
    setRotating(true);
    const res = await api.patch<{ inboundAddress?: string }>(`/api/orgs/${data.org.id}`, {
      rotateInboundToken: true,
    });
    setRotating(false);
    setConfirmingRotate(false);
    if (res.ok) {
      await load();
      setRotateMsg({
        ok: true,
        text: `New address: ${res.data.inboundAddress}. Update your email filter to forward here.`,
      });
    } else {
      setRotateMsg({ ok: false, text: res.error });
    }
  }

  if (loadError) {
    return (
      <div className="col" style={{ paddingTop: 80 }}>
        <Logo height={26} />
        <h1 className="h1" style={{ marginTop: 40 }}>Sign in required</h1>
        <p className="body" style={{ margin: "14px 0 24px" }}>
          You need to sign in to view this dashboard. A link on its own isn&rsquo;t enough.
        </p>
        <Link to="/login" className="btn">Sign in</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="col" style={{ paddingTop: 80 }}>
        <Logo height={26} to={null} />
        <div className="note note--wait" role="status" style={{ marginTop: 40 }}>
          <span className="spin" /> Loading…
        </div>
      </div>
    );
  }

  const org = data.org;
  const stats = data.stats;
  const rows = list?.rows ?? [];
  const proxyLine =
    org.proxy_type === "uen" ? `UEN ${org.paynow_proxy}` : `PayNow +${org.paynow_proxy}`;
  const publicUrl = `${origin}/${org.slug}`;

  return (
    <div>
      <header className="wrap wrap--dash dash-header">
        {/* signed in, so home is this page: back to Requests, from the top */}
        <Logo height={24} to="/dashboard" onClick={() => showTab("requests", true)} />
        <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
          <span className="hide-sm" style={{ fontSize: 15, fontWeight: 600 }}>{org.name}</span>
          <button type="button" className="textbtn" style={{ fontSize: 15 }} onClick={logout}>Sign out</button>
        </div>
      </header>

      <main className="wrap wrap--dash" style={{ paddingTop: 40, paddingBottom: 80 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: "16px 32px", marginBottom: 32 }}>
          <div>
            <h1 className="h1" ref={headingRef} tabIndex={-1}>{tab === "requests" ? "Requests" : tab === "embed" ? "Embed a button" : "Settings"}</h1>
            <p className="mono" style={{ margin: "12px 0 0", fontSize: 13, color: "var(--ink3)" }}>{proxyLine}</p>
          </div>
          <div className="dtabs" role="group" aria-label="Dashboard sections">
            <button type="button" className="dtab" aria-pressed={tab === "requests"} onClick={() => showTab("requests", true)}>Requests</button>
            <button type="button" className="dtab" aria-pressed={tab === "embed"} onClick={() => showTab("embed", true)}>
              Embed{!embedSeen && <span className="newtag">New</span>}
            </button>
            <button type="button" className="dtab" aria-pressed={tab === "settings"} onClick={() => showTab("settings", true)}>Settings</button>
          </div>
        </div>

        {org.approval === "pending" && (
          <div className="note note--wait" role="status" style={{ marginTop: 0, marginBottom: 28, alignItems: "flex-start", lineHeight: 1.5 }}>
            <ClockIcon size={20} strokeWidth={2} />
            <span>
              <b>Waiting for approval.</b> A person is checking {org.proxy_type === "uen" ? "your UEN" : "your PayNow number"}. Your payment link and QR codes switch on once they approve, and we will email {org.email ?? "you"}.
            </span>
          </div>
        )}

        {tab === "requests" && (
          <>
            {!embedSeen && (
              <div className="nudge" role="region" aria-label="Embed a button">
                <div className="nudge-text">
                  <strong>Put a Pay button on your website or form.</strong>
                  <span>Visitors click it, a ConfirmPay window opens, and they pay you by PayNow. Your public link stays for sending to people.</span>
                </div>
                <button type="button" className="btn btn--sm" onClick={() => { markEmbedSeen(); showTab("embed"); }}>Get embed code</button>
                <button type="button" className="textbtn" style={{ fontSize: 14 }} onClick={markEmbedSeen}>Not now</button>
              </div>
            )}

            {/* the three numbers that matter */}
            <section className="stats" aria-label="Summary">
              <div className="stat">
                <div className="k"><span className="ico ico--wait" style={{ width: 22, height: 22 }}><ClockIcon size={12} /></span>Waiting for payment</div>
                <div className="v">{money(stats.waiting.total)}</div>
                <div className="s">{count(stats.waiting.count)} request{stats.waiting.count === 1 ? "" : "s"}</div>
              </div>
              <div className="stat">
                <div className="k"><span className="ico ico--paid" style={{ width: 22, height: 22 }}><CheckIcon size={12} strokeWidth={2.6} /></span>Paid this month</div>
                <div className="v">{money(stats.paidThisMonth.total)}</div>
                <div className="s">{count(stats.paidThisMonth.count)} payment{stats.paidThisMonth.count === 1 ? "" : "s"}</div>
              </div>
              <div className="stat">
                <div className="k"><span className="ico ico--exp" style={{ width: 22, height: 22 }}><DashIcon size={12} /></span>Expired</div>
                <div className="v">{count(stats.counts.expired)}</div>
                <div className="s" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  Not paid within 3 hours
                  {stats.counts.expired > 0 && !confirmingBulk && (
                    <button
                      type="button"
                      id="delete-expired"
                      className="textbtn textbtn--danger"
                      onClick={() => {
                        setConfirming(null);
                        setEditingEmailId(null);
                        setNotice(null);
                        setConfirmingBulk(true);
                      }}
                    >
                      Delete expired ({count(stats.counts.expired)})
                    </button>
                  )}
                </div>
              </div>
            </section>

            {confirmingBulk && (
              <div
                className="confirm-bar confirm-bar--page"
                role="group"
                aria-label="Confirm deleting expired requests"
                onKeyDown={(e) => {
                  if (e.key === "Escape") cancelBulk();
                }}
              >
                <p>
                  Delete all {stats.counts.expired} expired request{stats.counts.expired === 1 ? "" : "s"}? This can&rsquo;t be undone. Paid requests are never deleted.
                </p>
                <button type="button" className="sbtn sbtn--solid" disabled={bulkBusy} onClick={deleteExpired}>
                  {bulkBusy ? "Deleting…" : `Delete ${stats.counts.expired}`}
                </button>
                <button type="button" className="sbtn" autoFocus disabled={bulkBusy} onClick={cancelBulk}>Cancel</button>
              </div>
            )}

            <div role="status">
              {notice && (
                <div className={"note " + (notice.ok ? "note--ok" : "note--err")} style={{ marginTop: 16, fontSize: 14.5 }}>
                  {notice.text}
                </div>
              )}
            </div>

            <div className="dash-main" style={{ marginTop: 40 }}>
              {/* ledger, with the export bar just above it */}
              <div style={{ minWidth: 0 }}>
              <ExportBar total={stats.counts.all} />
              <section
                ref={ledgerRef}
                className={"ledger" + (listBusy ? " ledger--busy" : "")}
                aria-label="Payment requests"
                aria-busy={listBusy}
              >
                <div className="lhead">
                  <div className="filters" role="group" aria-label="Filter by status">
                    {FILTERS.map((f) => (
                      <button key={f.id} type="button" className="fbtn" aria-pressed={filter === f.id} onClick={() => changeFilter(f.id)}>
                        {f.label}<span>{count(stats.counts[f.id])}</span>
                      </button>
                    ))}
                  </div>
                  <div style={{ flex: "1 1 220px", maxWidth: 300 }}>
                    <label className="sr-only" htmlFor="d-search">Search requests</label>
                    <input
                      id="d-search"
                      ref={searchRef}
                      className="inp inp--sm"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search reference, name, email"
                    />
                  </div>
                </div>

                <div className="dhead" aria-hidden="true">
                  <span />
                  <span>Request</span>
                  <span className="payer">Payer</span>
                  <span style={{ textAlign: "right" }}>Amount</span>
                  <span>Status</span>
                </div>

                {listError && (
                  <div className="error-box" role="alert" style={{ margin: "16px 24px" }}>{listError}</div>
                )}
                {list === null && !listError && <div className="empty">Loading…</div>}
                {list !== null && rows.length === 0 && (
                  <div className="empty">
                    {stats.counts.all === 0
                      ? "No requests yet. Create one, or share your public link."
                      : "Nothing matches that."}
                  </div>
                )}

                {rows.map((r) => {
                  const open = confirming?.id === r.id;
                  const editing = editingEmailId === r.id;
                  const expired = r.status === "expired";
                  const settleable = r.status === "pending" || expired;
                  const short =
                    r.status === "confirmed" &&
                    r.confirmed_amount != null &&
                    r.amount != null &&
                    r.confirmed_amount !== r.amount;
                  return (
                    <div className={"dwrap" + (open || editing ? " dwrap--open" : "")} key={r.id}>
                      <div className="drow">
                        <StatusIcon status={r.status} />
                        <div style={{ minWidth: 0 }}>
                          <div className="lt" style={expired ? { color: "var(--ink3)" } : undefined}>
                            {r.description || "Payment request"}
                          </div>
                          <div className="ls mono">{r.reference} &middot; {whenLabel(r)}</div>
                        </div>
                        <div className="payer" style={{ fontSize: 15, color: expired ? "var(--ink3)" : undefined }}>
                          {r.payer_name ?? "—"}
                        </div>
                        <div className="mono la" style={expired ? { color: "var(--ink3)" } : undefined}>
                          {r.amount != null ? money(r.amount) : "—"}
                          {short && (
                            <div style={{ fontSize: 12, color: "var(--amber-ink)", fontWeight: 600 }}>
                              got {money(r.confirmed_amount as number)}
                            </div>
                          )}
                        </div>
                        <div className="statuscell">
                          <StatusChip status={r.status} />
                          {r.status === "confirmed" && r.confirmed_manually && (
                            <div className="ls" style={{ marginTop: 4 }}>Marked by hand</div>
                          )}
                        </div>

                        {!open && !editing && (
                          <div className="acts">
                            {!expired && (
                              <CopyButton text={`${origin}/pay/${r.id}`} className="sbtn">Copy link</CopyButton>
                            )}
                            {settleable && (
                              <button type="button" id={`act-paid-${r.id}`} className="sbtn" disabled={busyId === r.id} onClick={() => openQuestion("paid", r.id)}>
                                Mark paid
                              </button>
                            )}
                            <button type="button" id={`act-email-${r.id}`} className="sbtn" disabled={busyId === r.id} onClick={() => startEditEmail(r.id)}>
                              {r.payer_email ? "Edit email" : "Add email"}
                            </button>
                            {settleable && (
                              <button type="button" id={`act-delete-${r.id}`} className="sbtn sbtn--danger" disabled={busyId === r.id} onClick={() => openQuestion("delete", r.id)}>
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {open && confirming && (
                        <div
                          className="confirm-bar"
                          style={confirming.kind === "paid" ? { background: "var(--sage-tint)" } : undefined}
                          role="group"
                          aria-label={`Confirm ${confirming.kind === "delete" ? "deleting" : "marking paid"} ${r.reference}`}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") cancelQuestion();
                          }}
                        >
                          <p>
                            {confirming.kind === "delete"
                              ? `Delete ${r.reference}?${r.status === "pending" ? " If the payer has already paid, it won’t be confirmed." : ""}`
                              : `Mark ${r.reference} as paid? Do this only after seeing the money in your own bank app.`}
                          </p>
                          {confirming.kind === "delete" ? (
                            <button type="button" className="sbtn sbtn--solid" disabled={busyId === r.id} onClick={() => deleteRequest(r.id)}>
                              {busyId === r.id ? "Deleting…" : "Delete"}
                            </button>
                          ) : (
                            <button type="button" className="sbtn sbtn--ink" disabled={busyId === r.id} onClick={() => markConfirmed(r.id)}>
                              {busyId === r.id ? "Saving…" : "Mark paid"}
                            </button>
                          )}
                          <button type="button" className="sbtn" autoFocus disabled={busyId === r.id} onClick={cancelQuestion}>Cancel</button>
                        </div>
                      )}

                      {editing && (
                        <>
                          <div className="edit-email">
                            <label className="sr-only" htmlFor={`em-${r.id}`}>Payer email for {r.reference}</label>
                            <input
                              id={`em-${r.id}`}
                              className="inp inp--sm"
                              type="email"
                              autoFocus
                              value={emailDraft}
                              onChange={(e) => setEmailDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveEmail(r.id);
                                if (e.key === "Escape") cancelEditEmail();
                              }}
                              placeholder="payer@example.com"
                              aria-invalid={emailError ? true : undefined}
                            />
                            <button type="button" className="sbtn sbtn--ink" style={{ height: 40 }} disabled={busyId === r.id} onClick={() => saveEmail(r.id)}>
                              {busyId === r.id ? "Saving…" : "Save"}
                            </button>
                            <button type="button" className="sbtn" style={{ height: 40 }} disabled={busyId === r.id} onClick={cancelEditEmail}>Cancel</button>
                          </div>
                          {emailError && (
                            <div className="error-box" role="alert" style={{ margin: "0 24px 14px" }}>{emailError}</div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}

                <Pager page={page} pageSize={pageSize} total={list?.total ?? 0} onPage={goToPage} onPageSize={changePageSize} />
              </section>
              </div>

              {/* right rail */}
              <aside style={{ display: "grid", gap: 20 }}>
                <section className="card card--strong">
                  <h2>New payment request</h2>
                  <form onSubmit={createRequest}>
                    <div className="field" style={{ marginBottom: 16 }}>
                      <label className="lbl" htmlFor="n-amt">Amount</label>
                      <div className="prefix-wrap">
                        <span className="prefix">S$</span>
                        <input
                          id="n-amt"
                          ref={amountInputRef}
                          className="inp inp--mono"
                          type="number"
                          inputMode="decimal"
                          min="0.01"
                          step="0.01"
                          required
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="20.00"
                        />
                      </div>
                    </div>
                    <div className="field" style={{ marginBottom: 16 }}>
                      <label className="lbl" htmlFor="n-desc">What is it for? <span className="opt">(optional)</span></label>
                      <input id="n-desc" className="inp" maxLength={140} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Monthly donation" />
                    </div>
                    <div className="field" style={{ marginBottom: 20 }}>
                      <label className="lbl" htmlFor="n-name">Payer name</label>
                      <input id="n-name" className="inp" maxLength={80} required value={payerName} onChange={(e) => setPayerName(e.target.value)} placeholder="Sofia N." />
                      <p className="hint">A backup way to match the payment if your bank&rsquo;s email has no reference.</p>
                    </div>
                    <button className="btn btn--block" style={{ height: 50 }} disabled={creating || !payerName.trim() || !amount.trim() || org.approval === "pending"}>
                      {creating ? "Creating…" : "Create request"}
                    </button>
                    {createError && <div className="error-box" role="alert">{createError}</div>}
                  </form>
                </section>

                <section className="card">
                  <h2>Your public link</h2>
                  <div className="copybox copybox--quiet">
                    <span className="mono">{`confirmpay.link/${org.slug}`}</span>
                    <CopyButton text={publicUrl} className="copybtn copybtn--quiet">Copy</CopyButton>
                  </div>
                  <div className="switch-row" style={{ marginTop: 18 }}>
                    <input
                      id="d-switch"
                      type="checkbox"
                      role="switch"
                      className="switch"
                      checked={org.public_link_enabled}
                      disabled={linkBusy}
                      onChange={(e) => toggleLink(e.target.checked)}
                    />
                    <label htmlFor="d-switch">Accepting payments</label>
                  </div>
                  <p className="hint">
                    Anyone with this link can create their own request. Switch it off to pause, for example when a campaign ends.
                  </p>
                  {linkError && <div className="error-box" role="alert">{linkError}</div>}
                  <div className="rail-more">
                    <button type="button" className="textbtn" style={{ fontSize: 14 }} onClick={() => showTab("embed")}>
                      Want it on your website or form? Get a button
                    </button>
                  </div>
                </section>

                <section className="card">
                  <h2>Bank emails</h2>
                  <div className="copybox copybox--quiet">
                    <span className="mono">{org.inbound_address}</span>
                    <CopyButton text={org.inbound_address} className="copybtn copybtn--quiet">Copy</CopyButton>
                  </div>
                  <p className="hint">
                    Your bank&rsquo;s payment emails are forwarded here.{" "}
                    {!confirmingRotate && (
                      <button type="button" className="textbtn" style={{ fontSize: 13 }} onClick={() => { setRotateMsg(null); setConfirmingRotate(true); }}>
                        Rotate address
                      </button>
                    )}
                  </p>
                  {data.lastEmail && (() => {
                    const note = bankEmailNote(data.lastEmail.outcome);
                    return (
                      <p className="hint" style={note.tone === "warn" ? { color: "var(--amber-ink)", fontWeight: 600 } : undefined}>
                        Last email, {timeAgo(data.lastEmail.at)}: {note.text}
                      </p>
                    );
                  })()}
                  {confirmingRotate && (
                    <div
                      className="confirm-bar confirm-bar--page"
                      style={{ background: "var(--sunk)" }}
                      role="group"
                      aria-label="Confirm rotating the forwarding address"
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setConfirmingRotate(false);
                      }}
                    >
                      <p>Rotate your forwarding address? You will need to point your email filter at the new one.</p>
                      <button type="button" className="sbtn sbtn--ink" disabled={rotating} onClick={rotateAddress}>
                        {rotating ? "Rotating…" : "Rotate"}
                      </button>
                      <button type="button" className="sbtn" autoFocus disabled={rotating} onClick={() => setConfirmingRotate(false)}>Cancel</button>
                    </div>
                  )}
                  {rotateMsg && (
                    <div className={"note " + (rotateMsg.ok ? "note--ok" : "note--err")} role="status" style={{ marginTop: 14, fontSize: 13.5, alignItems: "flex-start", wordBreak: "break-all" }}>
                      {rotateMsg.text}
                    </div>
                  )}
                </section>
              </aside>
            </div>
          </>
        )}

        {tab === "embed" && (
          <EmbedTab
            org={org}
            origin={origin}
            linkBusy={linkBusy}
            linkError={linkError}
            onToggleLink={toggleLink}
            onCopied={markEmbedSeen}
          />
        )}

        {tab === "settings" && (
          <>
            <Settings org={org} onSaved={load} />
            <SignOutEverywhere />
            <YourData />
          </>
        )}
      </main>

      {created && (
        <div className="modal-backdrop" onClick={() => setCreated(null)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-request-title"
            ref={createModalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="create-request-title">Request created</h2>
              <button type="button" className="x" aria-label="Close" ref={createCloseRef} onClick={() => setCreated(null)}>
                <CloseIcon />
              </button>
            </div>

            <QR payload={created.payload} downloadName={`paynow-${created.reference}.png`} />
            <div className="mono" style={{ textAlign: "center", marginTop: 12, fontSize: 14, fontWeight: 500 }}>
              {created.reference}
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <CopyButton text={created.payUrl} className="btn btn--ghost btn--sm">Copy payer link</CopyButton>
              <a href={created.payUrl} target="_blank" rel="noreferrer" className="btn btn--ghost btn--sm">
                Open <ExternalIcon size={16} />
              </a>
            </div>
            <button type="button" className="btn btn--block" style={{ marginTop: 18 }} onClick={() => setCreated(null)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Settings({ org, onSaved }: { org: SafeOrg; onSaved: () => void }) {
  const [name, setName] = useState(org.name);
  const [email, setEmail] = useState(org.email ?? "");
  const [refPrefix, setRefPrefix] = useState(org.ref_prefix);
  const [slug, setSlug] = useState(org.slug);
  const slugValue = slug.trim();
  const slugCheck = useSlugCheck(slugValue, org.slug);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<Notice | null>(null);

  // A new contact email is the address this dashboard is signed in to, so it
  // has to be confirmed first: Save emails a code to it, then asks for the code.
  const newEmail = email.trim();
  const emailChanged = newEmail !== "" && newEmail.toLowerCase() !== (org.email ?? "").toLowerCase();
  const [codeFor, setCodeFor] = useState<{ email: string; note: string } | null>(null);
  const [code, setCode] = useState("");
  const asking = emailChanged && codeFor !== null && codeFor.email === newEmail.toLowerCase();

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);

    let emailProof: string | undefined;
    if (emailChanged) {
      setSaving(true);
      if (!asking) {
        const sent = await api.post<{ ok: boolean; devCode?: string }>(`/api/orgs/${org.id}/email-code`, { email: newEmail });
        setSaving(false);
        if (!sent.ok) {
          setMsg({ ok: false, text: sent.error });
          return;
        }
        setCodeFor({
          email: newEmail.toLowerCase(),
          note: sent.data.devCode ? `Dev mode — your code is ${sent.data.devCode}.` : `We emailed a 6-digit code to ${newEmail}.`,
        });
        setCode(sent.data.devCode ?? "");
        return;
      }
      if (code.length !== 6) {
        setSaving(false);
        setMsg({ ok: false, text: "Enter the 6-digit code we emailed to the new address." });
        return;
      }
      const checked = await api.post<{ proof: string }>("/api/auth/verify-email", { email: newEmail, code });
      if (!checked.ok) {
        setSaving(false);
        setMsg({ ok: false, text: checked.error });
        return;
      }
      emailProof = checked.data.proof;
    }

    setSaving(true);
    const res = await api.patch<{ updated: string[] }>(`/api/orgs/${org.id}`, {
      name: name !== org.name ? name : undefined,
      email: emailChanged ? newEmail : undefined,
      emailProof,
      refPrefix: refPrefix !== org.ref_prefix ? refPrefix : undefined,
      slug: slugValue !== org.slug ? slugValue.toLowerCase() : undefined,
    });
    setSaving(false);
    if (res.ok) {
      const names: Record<string, string> = {
        ref_prefix: "reference prefix",
        public_link_enabled: "public link switch",
        email: "contact email",
      };
      setMsg({ ok: true, text: `Saved: ${res.data.updated.map((k) => names[k] ?? k).join(", ")}.` });
      setCodeFor(null);
      setCode("");
      onSaved();
    } else {
      setMsg({ ok: false, text: res.error });
    }
  }

  return (
    <form onSubmit={save} className="card" style={{ maxWidth: 640, padding: 32 }}>
      <div className="field">
        <label className="lbl" htmlFor="s-name">Organisation or business name</label>
        <input id="s-name" className="inp" maxLength={25} value={name} onChange={(e) => setName(e.target.value)} />
        <p className="hint">Shown on the PayNow QR your payers scan. 25 characters max.</p>
      </div>
      <div className="field">
        <label className="lbl" htmlFor="s-email">Contact email</label>
        <input id="s-email" className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@organisation.org" />
        <p className="hint">Used to sign in at /login. A new address is confirmed with a code before it replaces this one.</p>
      </div>
      {asking && codeFor && (
        <div className="field">
          <label className="lbl" htmlFor="s-code">6-digit code</label>
          <input
            id="s-code"
            className="inp codein"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="000000"
          />
          <p className="hint">
            {codeFor.note} Your contact email changes once it is entered.{" "}
            <button type="button" className="textbtn" style={{ fontSize: 13 }} onClick={() => { setEmail(org.email ?? ""); setCodeFor(null); setCode(""); }}>
              Keep my current email
            </button>
          </p>
        </div>
      )}
      <div className="field">
        <label className="lbl" htmlFor="s-prefix">Reference prefix</label>
        <input id="s-prefix" className="inp inp--mono" style={{ maxWidth: 200 }} maxLength={8} value={refPrefix} onChange={(e) => setRefPrefix(e.target.value.toUpperCase())} />
        <p className="hint">Every payment gets a reference like <span className="mono">{refPrefix || "DON"}-001</span>.</p>
      </div>
      <div className="field">
        <label className="lbl" htmlFor="s-slug">Public link</label>
        <input
          id="s-slug"
          className="inp"
          maxLength={32}
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          aria-describedby="slug-hint"
          aria-invalid={slugCheck.kind === "bad"}
        />
        <p id="slug-hint" className="hint" aria-live="polite">
          {slugCheck.kind === "checking" && "Checking…"}
          {slugCheck.kind === "bad" && <span className="bad">{slugCheck.text}</span>}
          {slugCheck.kind === "ok" && <span className="ok">confirmpay.link/{slugValue} is available.</span>}
          {(slugCheck.kind === "idle" || slugCheck.kind === "unknown") && (
            <>confirmpay.link/{slug || "…"}: payers can go straight here to fill in their own amount and pay you.</>
          )}
        </p>
        {slugValue !== org.slug && slugCheck.kind !== "bad" && (
          <p className="hint">
            Your current link, confirmpay.link/{org.slug}, will keep working and stay reserved for you.
          </p>
        )}
      </div>
      <button className="btn" disabled={saving || slugCheck.kind === "checking" || slugCheck.kind === "bad" || (asking && code.length !== 6)}>
        {saving ? "Working…" : asking ? "Confirm and save" : emailChanged ? "Email me a code" : "Save changes"}
      </button>
      <div role="status">
        {msg && <div className={"note " + (msg.ok ? "note--ok" : "note--err")}>{msg.text}</div>}
      </div>
    </form>
  );
}

/** Where to go to delete data, and what the policy says. */
function YourData() {
  return (
    <section className="card" style={{ maxWidth: 640, padding: 32, marginTop: 24 }}>
      <h2>Your data</h2>
      <p className="hint" style={{ margin: "0 0 20px" }}>
        Want to close this organisation, or clear the payer names and emails on your requests? Ask here and it is done within
        30 days. Export your requests first if you want a copy.
      </p>
      <Link to="/delete-data" className="btn btn--ghost">
        Delete my data
      </Link>
      <p className="hint" style={{ margin: "18px 0 0" }}>
        How we handle it is in the <Link to="/privacy" style={{ fontWeight: 600 }}>Privacy Policy</Link>.
      </p>
    </section>
  );
}

/** Ends every other signed-in session for this organisation. */
function SignOutEverywhere() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Notice | null>(null);

  async function signOutEverywhere() {
    setBusy(true);
    const res = await api.post("/api/auth/logout-all", {});
    setBusy(false);
    setConfirming(false);
    setMsg(
      res.ok
        ? { ok: true, text: "Done. Every other browser and device is signed out. You are still signed in here." }
        : { ok: false, text: res.error },
    );
  }

  return (
    <section className="card" style={{ maxWidth: 640, padding: 32, marginTop: 24 }}>
      <h2>Signed-in devices</h2>
      <p className="hint" style={{ margin: "0 0 20px" }}>
        Used a shared computer, lost a laptop, or someone who had access has left? This signs out every other browser
        and device that has this dashboard open. They will need a new email code to get back in. You stay signed in here.
      </p>
      {!confirming && (
        <button type="button" className="btn btn--ghost" onClick={() => { setMsg(null); setConfirming(true); }}>
          Sign out everywhere else
        </button>
      )}
      {confirming && (
        <div
          className="confirm-bar confirm-bar--page"
          style={{ background: "var(--sunk)", margin: 0 }}
          role="group"
          aria-label="Confirm signing out everywhere"
          onKeyDown={(e) => {
            if (e.key === "Escape") setConfirming(false);
          }}
        >
          <p>Sign out every other device?</p>
          <button type="button" className="sbtn sbtn--ink" disabled={busy} onClick={signOutEverywhere}>
            {busy ? "Signing out…" : "Sign out"}
          </button>
          <button type="button" className="sbtn" autoFocus disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
        </div>
      )}
      <div role="status">
        {msg && <div className={"note " + (msg.ok ? "note--ok" : "note--err")}>{msg.text}</div>}
      </div>
    </section>
  );
}
